'use client';

import { useEffect, useId, useState, useTransition } from 'react';

import { Check, ChevronsUpDown, User, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Button } from '@kit/ui/button';
import { Label } from '@kit/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@kit/ui/popover';
import { cn } from '@kit/ui/utils';

import type { ChildAccount, SelectedAccount } from '../_lib/types';
import { useBookingPresentation } from './booking-presentation';

// Re-export for external consumers like package-checkout-dialog
export type { SelectedAccount } from '../_lib/types';

interface AccountMultiSelectorProps {
  userId: string;
  userName?: string;
  tenantId?: string;
  selectedAccounts: SelectedAccount[];
  onChange: (accounts: SelectedAccount[]) => void;
  multiple?: boolean;
  className?: string;
}

/**
 * AccountMultiSelector - Multi-select dropdown for choosing who to purchase packages for
 *
 * Displays a dropdown with checkboxes for the user and their children.
 * Set multiple to false to select one account and close the dropdown.
 * If the user has no children, this component renders nothing.
 *
 * @param userId - The authenticated user's ID (parent account ID)
 * @param userName - The authenticated user's name (optional, defaults to "Myself")
 * @param tenantId - Optional tenant ID to filter children by tenant
 * @param selectedAccounts - Array of currently selected accounts
 * @param onChange - Callback when selection changes
 * @param className - Optional CSS classes
 */
export function AccountMultiSelector({
  userId,
  userName,
  tenantId,
  selectedAccounts,
  onChange,
  multiple = true,
  className,
}: AccountMultiSelectorProps) {
  const selectorId = useId();
  const presentation = useBookingPresentation();
  const client = useSupabase();
  const { t } = useTranslation();
  const [children, setChildren] = useState<ChildAccount[]>([]);
  const [isLoading, startTransition] = useTransition();
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Load children on mount
  useEffect(() => {
    if (!userId) return;

    startTransition(async () => {
      try {
        const { data, error } = await (client.rpc as any)('get_user_children', {
          p_user_id: userId,
        });

        if (error) {
          console.error('Error loading children:', error);
          setChildren([]);
        } else {
          const typedData = (data || []) as ChildAccount[];
          const filteredChildren = tenantId
            ? typedData.filter((c) => c.tenant_id === tenantId)
            : typedData;
          setChildren(filteredChildren);
        }
      } catch (error) {
        console.error('Error loading children:', error);
        setChildren([]);
      } finally {
        setHasLoaded(true);
      }
    });
  }, [userId, tenantId, client]);

  // Once children are known, reconcile the pre-seeded selection:
  // - No children → keep self selected (or select self if empty).
  // - Has children → clear the pre-seeded self so the user must pick explicitly.
  //   Runs only once per load; guarded by hasReconciled so user picks aren't clobbered.
  const [hasReconciled, setHasReconciled] = useState(false);
  useEffect(() => {
    if (!hasLoaded || hasReconciled) return;
    if (children.length === 0) {
      if (selectedAccounts.length === 0) {
        onChange([
          {
            id: userId,
            name: userName || t('bookings:bookForSelf'),
            isChild: false,
          },
        ]);
      }
    } else {
      const onlySelfSelected =
        selectedAccounts.length === 1 && selectedAccounts[0]?.id === userId;
      if (onlySelfSelected) {
        onChange([]);
      }
    }
    setHasReconciled(true);
  }, [
    hasLoaded,
    hasReconciled,
    children.length,
    selectedAccounts,
    userId,
    userName,
    onChange,
    t,
  ]);

  // Render nothing until we know there are children. Avoids a loading-skeleton
  // flash for the common "no children" case — the selector just appears if
  // children exist, otherwise stays hidden.
  if (!hasLoaded || children.length === 0) {
    return null;
  }

  const isSelected = (accountId: string) =>
    selectedAccounts.some((acc) => acc.id === accountId);

  const toggleAccount = (account: SelectedAccount) => {
    if (!multiple) {
      onChange([account]);
      setIsOpen(false);
      return;
    }

    if (isSelected(account.id)) {
      // Don't allow deselecting if it's the last one
      if (selectedAccounts.length === 1) return;
      onChange(selectedAccounts.filter((acc) => acc.id !== account.id));
    } else {
      onChange([...selectedAccounts, account]);
    }
  };

  const getDisplayText = () => {
    if (!multiple) {
      return selectedAccounts[0]?.name || t('bookings:selectAccount');
    }

    if (selectedAccounts.length === 0) {
      return t('bookings:selectAccounts');
    }
    if (selectedAccounts.length === 1) {
      return selectedAccounts[0]?.name || t('bookings:selectAccounts');
    }
    return t('bookings:accountsSelected', { count: selectedAccounts.length });
  };

  const allAccounts: SelectedAccount[] = [
    { id: userId, name: userName || t('bookings:bookForSelf'), isChild: false },
    ...children.map((child) => ({
      id: child.child_account_id,
      name: child.child_name,
      isChild: true,
    })),
  ];

  return (
    <div className={className}>
      <Label htmlFor={selectorId} className="mb-2 block text-sm font-medium">
        {t('bookings:purchaseFor')}
      </Label>

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            id={selectorId}
            type="button"
            variant="outline"
            aria-expanded={isOpen}
            className="w-full justify-between"
          >
            <span className="truncate">{getDisplayText()}</span>
            <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          {...presentation}
          className={cn('w-full p-0', presentation.className)}
          align="start"
        >
          <div className="max-h-60 overflow-auto">
            {allAccounts.map((account) => (
              <button
                key={account.id}
                type="button"
                aria-pressed={isSelected(account.id)}
                className={cn(
                  'hover:bg-accent flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-start',
                  isSelected(account.id) && 'bg-accent/50',
                )}
                onClick={() => toggleAccount(account)}
              >
                <div
                  className={cn(
                    'flex h-4 w-4 items-center justify-center rounded-sm border',
                    isSelected(account.id)
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'border-input',
                  )}
                >
                  {isSelected(account.id) && <Check className="h-3 w-3" />}
                </div>
                {account.isChild ? (
                  <Users className="text-muted-foreground h-4 w-4" />
                ) : (
                  <User className="text-muted-foreground h-4 w-4" />
                )}
                <span className="flex-1">{account.name}</span>
                {account.isChild && (
                  <span className="text-muted-foreground text-xs">
                    {t('bookings:childBadge')}
                  </span>
                )}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Show selected accounts summary */}
      {multiple && selectedAccounts.length > 1 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {selectedAccounts.map((account) => (
            <span
              key={account.id}
              className="bg-muted inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
            >
              {account.isChild ? (
                <Users className="h-3 w-3" />
              ) : (
                <User className="h-3 w-3" />
              )}
              {account.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
